import { PureComponent } from 'react';
import { layer, source, feature } from 'react-openlayers';

import { KML_FEATURE_TYPE } from '../../../featureTypes';
import { WHITE } from '../../../rgbColors';
import { CORE_DEADBAND_WIDTH_M } from '../../../constants';
import { MAP_TYPES, MapType } from '../helpers/mapTypes';
import { getCentroidKey, getSelectedFeature } from '../helpers/features';
import selectionManager from '../../../services/SelectionManager';
import { DefaultKMLFeatureProperties, KMLFeature, JobType } from '../../../types/types';
import { Sample } from '../../../db';
import EventBus from '../../../EventBus';
import { MAP_EVENTS } from '../../../mapEvents';
import { MISSION_EVENTS } from '../../../missionEvents';
import { SESSION_EVENTS } from '../../../sessionEvents';
import { getCurrentMission, getCurrentSession } from '../../../dataModelHelpers';
import { MeasuredCircle } from '../features/measured_circle';
import {
  MapDebugCloseTolerance,
  MapDebugNoSampleTolerance,
  MapDebugPresentTolerance,
  MapPixelsPerMeterStorage,
} from '../../../db/local_storage';

interface CentroidsLayerProps {
  jobType: JobType;
  zoneMission: boolean;
  type: MapType;
}

interface CentroidsLayerState {
  features: JSX.Element[];
  presentSampleId: string;
  activeSampleId: string | undefined;
}

const Z_INDEX_CENTROID = 4;

/**
 * CentroidsLayer — focused layer that renders ONLY the sample centroid
 * features of the current mission. Step 2 of the KmlLayer split. See
 * docs/superpowers/plans/2026-04-08-kml-layer-split.md.
 *
 * Owns its own EventBus subscriptions:
 *   - MISSION_EVENTS.CREATED_OR_DELETED (mission swap → full rebuild)
 *   - MAP_EVENTS.CORES_NEED_UPDATE (full rebuild — most core-change
 *     operations also move / add / remove samples or change their
 *     skipped state, so we piggy-back on the core rebuild signal)
 *   - MAP_EVENTS.SAMPLES_UPDATED (targeted rebuild on barcode scan /
 *     skip / rename / add / delete / box-clear / etc.)
 *   - MAP_EVENTS.SAMPLE_ARRIVED / SAMPLE_LEFT (present-sample highlight)
 *   - SESSION_EVENTS.UPDATED_CURRENT_SAMPLE (active-sample highlight;
 *     blue fill)
 *
 * The centroid "selected" visual (`selected ? 12 : 8` radius) is
 * restored via the shared SelectionManager singleton and
 * MAP_EVENTS.SELECTION_CHANGED. When selection changes we rebuild only
 * the centroids whose selected-ness flipped (old + new), so a click
 * doesn't redraw every sample.
 *
 * Intentionally NOT subscribing to:
 *   - MISSION_EVENTS.UPDATED — too noisy; many of its ~50 callers
 *     don't affect centroids (zone-rename, metadata edits, etc.).
 *     Sites that do affect centroids either fire
 *     `dispatchCoresNeedUpdate()` (full rebuild) or
 *     `dispatchSamplesUpdated([...])` (targeted).
 *   - BOX_EVENTS.ACTIVE_UPDATED — fires from ~9 sites; only
 *     `sample_ops.setBarcode` actually touches centroid-visible
 *     state (`bag_id`). That site now also fires
 *     `dispatchSamplesUpdated([sample_id])` directly.
 *   - BOX_EVENTS.SAMPLES_UPDATED — fires from
 *     `sample_box_ops.clearSamples` / `clearAllSamples`. Both sites
 *     now also fire `dispatchSamplesUpdated([ids])`.
 *   - BOX_EVENTS.MOVED_SAMPLE — only changes `sample.SampleBox_id`;
 *     centroid renders off `bag_id`, which is unchanged. False
 *     subscription, removed.
 */
export default class CentroidsLayer extends PureComponent<CentroidsLayerProps, CentroidsLayerState> {
  /**
   * Map from sample_id -> JSX features for that sample's centroid. Kept
   * alongside state.features so rebuildForSamples can replace only the
   * affected entries and preserve reference equality for untouched ones.
   */
  private featuresBySampleId: Map<string, JSX.Element[]> = new Map();

  /** Deterministic sample order used to flatten featuresBySampleId. */
  private sampleOrder: string[] = [];

  /** Microtask batch for rebuildForSamples — see CorePointsLayer. */
  private pendingRebuildIds: Set<string> | null = null;

  /**
   * Last-known selected centroid sample_id — used by onSelectionChanged
   * to compute the (old, new) pair for a targeted rebuild. Not state
   * because the visual always reads directly from the shared
   * SelectionManager at build time; this field only exists to scope
   * the rebuild.
   */
  private prevSelectedCentroidSampleId: string | undefined = undefined;

  constructor(props: CentroidsLayerProps) {
    super(props);

    this.state = {
      features: [],
      presentSampleId: '',
      activeSampleId: undefined,
    };
    this.prevSelectedCentroidSampleId = this.readSelectedCentroidSampleId();

    this.rebuildAll = this.rebuildAll.bind(this);
    this.onSamplesUpdated = this.onSamplesUpdated.bind(this);
    this.onCurrentSampleChanged = this.onCurrentSampleChanged.bind(this);
    this.onPresentSampleChanged = this.onPresentSampleChanged.bind(this);
    this.onPresentSampleLeft = this.onPresentSampleLeft.bind(this);
    this.onSelectionChanged = this.onSelectionChanged.bind(this);
  }

  componentDidMount() {
    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, this.rebuildAll);
    EventBus.on(MAP_EVENTS.CORES_NEED_UPDATE, this.rebuildAll);
    EventBus.on(MAP_EVENTS.SAMPLES_UPDATED, this.onSamplesUpdated);
    EventBus.on(MAP_EVENTS.SAMPLE_ARRIVED, this.onPresentSampleChanged, true);
    EventBus.on(MAP_EVENTS.SAMPLE_LEFT, this.onPresentSampleLeft, true);
    EventBus.on(SESSION_EVENTS.UPDATED_CURRENT_SAMPLE, this.onCurrentSampleChanged);
    EventBus.on(MAP_EVENTS.SELECTION_CHANGED, this.onSelectionChanged);
    this.rebuildAll();
  }

  componentDidUpdate(_prevProps: CentroidsLayerProps, prevState: CentroidsLayerState) {
    // presentSample toggles the "blinking" flag and offsetX on the
    // centroid label, activeSample toggles the blue fill. Rebuild just
    // the affected samples.
    if (this.state.presentSampleId !== prevState.presentSampleId) {
      const affected = [prevState.presentSampleId, this.state.presentSampleId].filter((id): id is string =>
        Boolean(id),
      );

      if (affected.length) {
        this.rebuildForSamples(affected);
      }
    }

    if (this.state.activeSampleId !== prevState.activeSampleId) {
      const affected = [prevState.activeSampleId, this.state.activeSampleId].filter((id): id is string => Boolean(id));

      if (affected.length) {
        this.rebuildForSamples(affected);
      }
    }
  }

  componentWillUnmount() {
    EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, this.rebuildAll);
    EventBus.remove(MAP_EVENTS.CORES_NEED_UPDATE, this.rebuildAll);
    EventBus.remove(MAP_EVENTS.SAMPLES_UPDATED, this.onSamplesUpdated);
    EventBus.remove(MAP_EVENTS.SAMPLE_ARRIVED, this.onPresentSampleChanged);
    EventBus.remove(MAP_EVENTS.SAMPLE_LEFT, this.onPresentSampleLeft);
    EventBus.remove(SESSION_EVENTS.UPDATED_CURRENT_SAMPLE, this.onCurrentSampleChanged);
    EventBus.remove(MAP_EVENTS.SELECTION_CHANGED, this.onSelectionChanged);
  }

  /**
   * Read the currently-selected centroid's sample_id from the shared
   * SelectionManager. Returns undefined if nothing is selected or the
   * selected feature is not a centroid.
   */
  private readSelectedCentroidSampleId(): string | undefined {
    const { selectedFeature, featureType } = getSelectedFeature(selectionManager.selectedFeatures);

    if (featureType !== KML_FEATURE_TYPE.CENTROID || !selectedFeature) {
      return undefined;
    }

    return getCentroidKey(selectedFeature.getProperties() as { name?: string });
  }

  onSelectionChanged() {
    const next = this.readSelectedCentroidSampleId();
    const prev = this.prevSelectedCentroidSampleId;

    if (next === prev) {
      return;
    }

    this.prevSelectedCentroidSampleId = next;
    // Targeted rebuild of just the (old, new) affected samples — the
    // rest of the list keeps its JSX references.
    const affected = [prev, next].filter((id): id is string => Boolean(id));

    if (affected.length) {
      this.rebuildForSamples(affected);
    }
  }

  // ---------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------

  onSamplesUpdated(sampleIdsString?: string) {
    if (!sampleIdsString) {
      return;
    }

    let sampleIds: string[] = [];

    try {
      const parsed = JSON.parse(sampleIdsString) as { sampleIds: string[] };
      sampleIds = Array.isArray(parsed?.sampleIds) ? parsed.sampleIds : [];
    } catch {
      return;
    }

    if (sampleIds.length === 0) {
      return;
    }

    this.rebuildForSamples(sampleIds);
  }

  onCurrentSampleChanged() {
    const session = getCurrentSession();
    const activeSampleId = session?.getSample()?.sample_id;

    if (activeSampleId !== this.state.activeSampleId) {
      this.setState({ activeSampleId });
    }
  }

  onPresentSampleChanged(sampleId: string) {
    if (this.state.presentSampleId === sampleId) {
      return;
    }

    this.setState({ presentSampleId: sampleId });
  }

  onPresentSampleLeft(sampleId: string) {
    if (this.state.presentSampleId !== sampleId) {
      return;
    }

    this.setState({ presentSampleId: '' });
  }

  // ---------------------------------------------------------------------
  // Rebuild paths
  // ---------------------------------------------------------------------

  /**
   * Cache the localStorage-backed debug flags once per rebuild pass.
   * Reading them per-centroid in the hot loop is ~0.3ms each in JSDOM;
   * with 127 samples that adds up fast.
   */
  private readRenderOptions() {
    return {
      pixelsPerMeter: MapPixelsPerMeterStorage.get(),
      mapDebugCloseTolerance: MapDebugCloseTolerance.get(),
      mapDebugPresentTolerance: MapDebugPresentTolerance.get(),
      mapDebugNoSampleTolerance: MapDebugNoSampleTolerance.get(),
    };
  }

  rebuildAll() {
    console.count('CentroidsLayer.rebuildAll');
    const mission = getCurrentMission();

    if (!mission) {
      this.featuresBySampleId = new Map();
      this.sampleOrder = [];
      this.setState({ features: [] });

      return;
    }

    const opts = this.readRenderOptions();

    const newMap = new Map<string, JSX.Element[]>();
    const order: string[] = [];

    for (const sample of mission.getSamples()) {
      const sampleJsx = this.buildFeaturesForSample(sample, opts);
      newMap.set(sample.sample_id, sampleJsx);
      order.push(sample.sample_id);
    }

    this.featuresBySampleId = newMap;
    this.sampleOrder = order;
    this.setState({ features: this.flattenFeatures() });
  }

  rebuildForSamples(sampleIds: string[]) {
    if (sampleIds.length === 0) {
      return;
    }

    if (!this.pendingRebuildIds) {
      this.pendingRebuildIds = new Set(sampleIds);
      queueMicrotask(() => {
        const ids = Array.from(this.pendingRebuildIds!);
        this.pendingRebuildIds = null;
        this.doRebuildForSamples(ids);
      });
    } else {
      for (const id of sampleIds) {
        this.pendingRebuildIds.add(id);
      }
    }
  }

  private doRebuildForSamples(sampleIds: string[]) {
    console.log('CentroidsLayer.rebuildForSamples', sampleIds);
    const mission = getCurrentMission();

    if (!mission || sampleIds.length === 0) {
      return;
    }

    const sampleById = new Map<string, Sample>();

    for (const s of mission.getSamples()) {
      sampleById.set(s.sample_id, s);
    }

    const opts = this.readRenderOptions();

    // Handle all three cases: create, update, delete/rename.
    // Mirrors CorePointsLayer.rebuildForSamples — see the comment
    // there for details.
    let anyChange = false;

    for (const id of sampleIds) {
      const sample = sampleById.get(id);

      if (sample) {
        const sampleJsx = this.buildFeaturesForSample(sample, opts);
        this.featuresBySampleId.set(id, sampleJsx);

        if (!this.sampleOrder.includes(id)) {
          this.sampleOrder.push(id);
        }

        anyChange = true;
      } else if (this.featuresBySampleId.has(id)) {
        this.featuresBySampleId.delete(id);
        this.sampleOrder = this.sampleOrder.filter((x) => x !== id);
        anyChange = true;
      }
    }

    if (!anyChange) {
      return;
    }

    this.setState({ features: this.flattenFeatures() });
  }

  // ---------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------

  private flattenFeatures(): JSX.Element[] {
    const out: JSX.Element[] = [];

    for (const sampleId of this.sampleOrder) {
      const sampleFeatures = this.featuresBySampleId.get(sampleId);

      if (sampleFeatures) {
        out.push(...sampleFeatures);
      }
    }

    return out;
  }

  private buildFeaturesForSample(sample: Sample, opts: ReturnType<CentroidsLayer['readRenderOptions']>): JSX.Element[] {
    const kmlFeature = sample.to_feature(false);

    if (!kmlFeature) {
      return [];
    }

    // Cast through any — KMLSampleFeatureProperties doesn't declare index
    (kmlFeature.properties as any).index = 0;

    return this.genCentroidFeature(kmlFeature as any, this.state.activeSampleId, opts);
  }

  /**
   * Port of KmlLayer.genCentroidFeature. Reads the "selected" flag
   * directly from the shared SelectionManager each call.
   */
  private genCentroidFeature(
    kmlFeature: KMLFeature<DefaultKMLFeatureProperties>,
    activeSampleId: string | undefined,
    opts: ReturnType<CentroidsLayer['readRenderOptions']>,
  ): JSX.Element[] {
    const key = getCentroidKey(kmlFeature.properties);
    // Read selection directly from the shared SelectionManager each
    // build — keeps the visual in sync even if prevSelectedCentroidSampleId
    // is stale (e.g. first rebuildAll before any SELECTION_CHANGED
    // fires). Cheap because it's a single array[0] lookup.
    const selected = key === this.readSelectedCentroidSampleId();
    const scanned = Boolean(kmlFeature.properties.bag_id);
    const activeSample = kmlFeature.properties.name === activeSampleId && this.props.type === MAP_TYPES.SAMPLING;
    const uniqueKey = `${key}-${KML_FEATURE_TYPE.CENTROID}`;
    const sampleId = kmlFeature.properties.name;
    const skipped = kmlFeature.properties.skipped;
    const centroidLabel = sampleId;
    const presentSample = sampleId === this.state.presentSampleId;
    const mission = getCurrentMission()!;
    const presentTolerance = mission.get_present_tolerance();

    if (!kmlFeature.geometry.coordinates) {
      return [];
    }

    return [
      <feature.PointReact
        coordinate={kmlFeature.geometry.coordinates}
        circleOptions={{
          radius: selected ? 12 : 8,
          strokeOptions: { color: 'black', width: 2 },
          fillOptions: { color: skipped ? 'red' : activeSample ? 'blue' : scanned ? '#43a047' : 'white' },
          blinking: presentSample,
        }}
        textOptions={{
          text: centroidLabel,
          font: presentSample ? '24px Calibri,sans-serif' : '16px Calibri,sans-serif',
          fillOptions: { color: WHITE },
          offsetX: presentSample ? 60 : 20,
          offsetY: presentSample ? 0 : 20 * kmlFeature.properties.site_order - 10,
        }}
        properties={{ ...kmlFeature.properties }}
        key={uniqueKey}
        zIndex={Z_INDEX_CENTROID}
        hideAtZoom={14}
      />,
      opts.mapDebugCloseTolerance && (
        <MeasuredCircle
          center={kmlFeature.geometry.coordinates}
          color="rgba(255, 128, 0, 0.1)"
          circleMeasurements
          innerRadiusMeters={presentTolerance}
          outerRadiusMeters={mission.get_close_tolerance()}
          pixelsPerMeter={opts.pixelsPerMeter}
        />
      ),
      opts.mapDebugPresentTolerance && (
        <MeasuredCircle
          center={kmlFeature.geometry.coordinates}
          color="rgba(0, 255, 0, 0.1)"
          outerRadiusMeters={presentTolerance}
          pixelsPerMeter={opts.pixelsPerMeter}
        />
      ),
      opts.mapDebugNoSampleTolerance && (
        <MeasuredCircle
          center={kmlFeature.geometry.coordinates}
          color="rgba(255, 0, 0, 0.1)"
          innerRadiusMeters={presentTolerance}
          outerRadiusMeters={presentTolerance + CORE_DEADBAND_WIDTH_M}
          pixelsPerMeter={opts.pixelsPerMeter}
        />
      ),
    ].filter((element) => !!element) as JSX.Element[];
  }

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  render() {
    return (
      // @ts-ignore - layer.Vector children typing
      <layer.VectorImage zIndex={Z_INDEX_CENTROID}>
        {/* @ts-ignore - source.VectorSourceReact children typing */}
        <source.VectorSourceReact>{this.state.features}</source.VectorSourceReact>
      </layer.VectorImage>
    );
  }
}
